
import { supabase } from '@/integrations/supabase/client';
import { Client, SocialProfiles } from '@/components/clients/types';

export const fetchClientById = async (clientId: string): Promise<Client> => {
  if (!clientId) {
    throw new Error('Client ID is required');
  }
  
  const { data, error } = await supabase
    .from('clients')
    .select(`
      *,
      jobs_clients (
        jobs (
          *
        )
      ),
      client_news_alerts (
        id,
        client_id,
        title,
        content,
        url,
        source,
        published_at,
        is_read,
        created_at,
        updated_at
      ),
      client_documents (
        id,
        client_id,
        document_name,
        document_type,
        category,
        file_url,
        file_size,
        file_type,
        uploaded_by,
        upload_date,
        expiry_date,
        is_template,
        version,
        parent_document_id,
        status,
        metadata,
        created_at,
        updated_at
      )
    `)
    .eq('id', clientId)
    .single();

  if (error) {
    console.error('Error fetching client:', error);
    throw error;
  }
  
  // Transform jobs data
  const clientData = data as any;
  if (clientData && clientData.jobs_clients) {
    clientData.jobs = clientData.jobs_clients
      .map((jc: any) => jc.jobs)
      .filter(Boolean)
      .map((job: any) => ({
        ...job,
        posted: job.posted || new Date().toISOString(),
      }));
    delete clientData.jobs_clients;
  } else {
    clientData.jobs = [];
  }

  // Ensure client_news_alerts is properly structured
  if (!clientData.client_news_alerts) {
    clientData.client_news_alerts = [];
  }

  // Ensure client_documents is properly structured
  if (!clientData.client_documents) {
    clientData.client_documents = [];
  }

  // Ensure all required fields are present with proper defaults
  const transformedClient: Client = {
    ...clientData,
    industry: clientData.industry || null,
    contact_email: clientData.contact_email || null,
    contact_phone: clientData.contact_phone || null,
    corporate_phone: clientData.corporate_phone || null,
    address: clientData.address || null,
    website: clientData.website || null,
    status: clientData.status || null,
    notes: clientData.notes || null,
    user_id: clientData.user_id || null,
    news_monitoring_enabled: clientData.news_monitoring_enabled ?? true,
    geographic_area: clientData.geographic_area || null,
    created_at: clientData.created_at || new Date().toISOString(),
    updated_at: clientData.updated_at || new Date().toISOString(),
    jobs: clientData.jobs || [],
    client_news_alerts: clientData.client_news_alerts || [],
    client_documents: clientData.client_documents || [],
    // New fields default values with type checking
    employee_count: clientData.employee_count || null,
    revenue_range: clientData.revenue_range || null,
    social_profiles: (clientData.social_profiles && typeof clientData.social_profiles === 'object' && !Array.isArray(clientData.social_profiles))
      ? clientData.social_profiles as SocialProfiles
      : null,
    headquarters_location: clientData.headquarters_location || null,
    founded_year: clientData.founded_year || null,
    technologies_used: Array.isArray(clientData.technologies_used)
      ? clientData.technologies_used.map(String)
      : null,
    description: clientData.description || null,
    // Add new enrichment fields
    enrichment_status: clientData.enrichment_status || 'none',
    last_enrichment_run_id: clientData.last_enrichment_run_id || null,
  };

  return transformedClient;
};
